import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { goToCommunity } from '../utils/navigation';
import { Shield, Users, Calendar, Settings, LogOut, Home, Plus, Upload, Search, TrendingUp, Eye, MapPin, CheckCircle } from 'lucide-react';
import { useRoleBasedEvents } from '../hooks/useRoleBasedEvents';
import EventCard from './EventCard';
import EventDetailModal from './EventDetailModal';
import Pagination from './Pagination';
import CommunityUserManagement from './CommunityUserManagement';
import BulkEventUpload from './BulkEventUpload';
import EventFormWithMap from './EventFormWithMap';
import { apiService } from '../services/api';
import { DASHBOARD_CONFIG } from '../config/dashboard';

// Committee Trending Events Section Component
const CommunityTrendingEventsSection: React.FC<{ loading: boolean }> = ({ loading }) => {
  const { token } = useAuth();
  const [trendingEvents, setTrendingEvents] = useState<any[]>([]);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrendingEvents = async () => {
      if (!token) return;
      
      setTrendingLoading(true);
      setError(null);
      try {
        const response = await apiService.getTrendingEvents({ limit: 4 }); // Get top 4 trending events for community
        if (response.success && response.data) {
          setTrendingEvents(response.data);
        } else {
          throw new Error('Failed to fetch trending events');
        }
      } catch (error) {
        console.error('Error fetching trending events:', error);
        setError('Failed to load trending events');
      } finally {
        setTrendingLoading(false);
      }
    };

    fetchTrendingEvents();
  }, [token]);

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Trending Events</h3>
          </div>
          <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
            🔥 Hot
          </span>
        </div>
      </div>
      <div className="p-6">
        {trendingLoading || loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 h-32 rounded-lg mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center text-red-500 py-8">
            <TrendingUp className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <p className="text-lg font-medium mb-2">Unable to Load Trending Events</p>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        ) : trendingEvents.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-2">No Trending Events</p>
            <p className="text-sm text-gray-500">Check back later for popular events</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {trendingEvents.map((event: any, index: number) => (
              <div key={event.id || index} className="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    #{index + 1}
                  </span>
                  {event.isVerified && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                </div>
                
                <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                  {event.title || 'Untitled Event'}
                </h4>
                
                <div className="space-y-1 text-sm text-gray-600">
                  {event.location && (
                    <div className="flex items-center space-x-1">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{event.location}</span>
                    </div>
                  )}
                  
                  {event.expectedAttendance && (
                    <div className="flex items-center space-x-1">
                      <Eye className="h-3 w-3" />
                      <span>{event.expectedAttendance.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {event.startDate && (
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(event.startDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {event.createdByRole || 'Committee'}
                    </span>
                    <div className="flex items-center space-x-1">
                      <TrendingUp className="h-3 w-3 text-red-500" />
                      <span className="text-xs text-red-600 font-medium">Trending</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CommunityAdminDashboard: React.FC = () => {
  const { user, logout, token } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'events' | 'community-users'>('overview');
  // Always use public events since we removed the source filter buttons
  const eventSource = 'public';
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  // We're not using gallery functionality currently but keeping for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  // Modal states for bulk upload and event creation
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [eventFormLoading, setEventFormLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEvents: DASHBOARD_CONFIG.DEFAULT_STATS.TOTAL_EVENTS,
    totalUsers: DASHBOARD_CONFIG.DEFAULT_STATS.TOTAL_USERS,
    pendingApprovals: DASHBOARD_CONFIG.DEFAULT_STATS.PENDING_APPROVALS,
    activeEvents: DASHBOARD_CONFIG.DEFAULT_STATS.ACTIVE_EVENTS,
    trendingEvents: 0
  });
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DASHBOARD_CONFIG.DEFAULT_PAGE_SIZE);

  // Fetch events based on user role and selected source
  const { 
    events: normalizedEvents, 
    loading: eventsLoading, 
    refetch: refetchEvents,
    pagination
  } = useRoleBasedEvents(eventSource, {
    page: currentPage,
    limit: pageSize,
    search: searchTerm || undefined,
    sortBy: 'CREATED_AT',
    sortOrder: 'DESC'
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch real stats from API
        if (token) {
          try {
            const statsResponse = await apiService.getEventStats(token);
            if (statsResponse.data) {
              setStats({
                totalEvents: statsResponse.data.totalEvents || 0,
                totalUsers: statsResponse.data.totalUsers || 0,
                pendingApprovals: statsResponse.data.pendingApprovals || 0,
                activeEvents: statsResponse.data.activeEvents || 0,
                trendingEvents: statsResponse.data.trendingEvents || 0
              });
            }
          } catch (error) {
            console.error('Error fetching stats:', error);
            // Keep default values if API fails
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  const handleLogout = () => {
    logout();
  };
  
  // Helper functions for event display formatting - currently used in the normalized events
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate) return 'Date TBA';
    
    try {
      const start = new Date(startDate);
      const end = endDate ? new Date(endDate) : start;
      
      const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
      const startDay = start.getDate();
      const endDay = end.getDate();
      const year = start.getFullYear();
      
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
      }
    } catch {
      return 'Date TBA';
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatAttendees = (count?: number) => {
    if (!count) return 'Open to all';
    if (count >= DASHBOARD_CONFIG.ATTENDEE_COUNT_THRESHOLD) {
      return `${Math.floor(count / DASHBOARD_CONFIG.ATTENDEE_COUNT_THRESHOLD)}${DASHBOARD_CONFIG.ATTENDEE_COUNT_SUFFIX}`;
    }
    return `${count}+`;
  };
  
  // Handle view event details
  const handleViewEventDetails = (event: any) => {
    setSelectedEvent(event);
    setShowEventModal(true);
    setGalleryIndex(0);
  };
  
  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  }, []);
  
  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleNavigation = (path: string) => {
    if (path === '/community') {
      goToCommunity();
    }
  };
  
  // Handle bulk upload success
  const handleBulkUploadSuccess = () => {
    refetchEvents();
    setShowBulkUploadModal(false);
  };
  
  // Handle event creation
  const handleCreateEvent = async (eventData: FormData) => {
    if (!token) return;
    
    setEventFormLoading(true);
    try {
      await apiService.createEvent(eventData, token);
      setShowCreateEventModal(false);
      refetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    } finally {
      setEventFormLoading(false);
    }
  };

  // Handle event editing
  const handleEditEvent = (event: any) => {
    setSelectedEvent(event);
    setShowEventModal(false);
    setShowEditModal(true);
  };

  // Handle event update
  const handleUpdateEvent = async (eventData: any) => {
    if (!selectedEvent || !token) return;
    
    setEventFormLoading(true);
    try {
      await apiService.updateEvent(selectedEvent.id || selectedEvent.EVENT_ID, eventData, token);
      setShowEditModal(false);
      setSelectedEvent(null);
      refetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    } finally {
      setEventFormLoading(false);
    }
  };

  if (!user || !['COMMITTEE_ADMIN'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this dashboard.</p>
        </div>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Events</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalEvents}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Shield className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingApprovals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Events</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeEvents}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Trending Events</p>
              <p className="text-2xl font-bold text-gray-900">{stats.trendingEvents}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <button
          onClick={() => setActiveTab('events')}
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow duration-200 text-left group"
        >
          <div className="flex items-center justify-between">
            <div>
              <Calendar className="h-8 w-8 text-dpn-pink mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Events</h3>
              <p className="text-gray-600">Create, edit, and manage community events</p>
            </div>
            <div className="text-dpn-pink group-hover:translate-x-1 transition-transform duration-200">
              →
            </div>
          </div>
        </button>

        <button
          onClick={() => setActiveTab('community-users')}
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow duration-200 text-left group"
        >
          <div className="flex items-center justify-between">
            <div>
              <Users className="h-8 w-8 text-dpn-pink mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">User Management</h3>
              <p className="text-gray-600">Manage community users and permissions</p>
            </div>
            <div className="text-dpn-pink group-hover:translate-x-1 transition-transform duration-200">
              →
            </div>
          </div>
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <Settings className="h-8 w-8 text-gray-400 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Settings</h3>
              <p className="text-gray-600">Configure community settings and preferences</p>
            </div>
            <div className="text-gray-400">
              →
            </div>
          </div>
        </div>
      </div>

      {/* Trending Events Section */}
      <CommunityTrendingEventsSection loading={loading} />

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dpn-pink mx-auto mb-4"></div>
                             <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.LOADING.RECENT_ACTIVITY}</p>
            </div>
          ) : (
            <div className="text-center py-8">
                             <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.NO_DATA.RECENT_ACTIVITY}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderEventsTab = () => (
    <>
      {/* Event Management Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Event Management</h2>
            <p className="text-gray-600">Approve/reject events and manage the Durga Puja Network</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={() => setShowBulkUploadModal(true)}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Upload className="h-4 w-4" />
              <span>Bulk Upload</span>
            </button>
            <button 
              onClick={() => setShowCreateEventModal(true)}
              className="flex items-center space-x-2 bg-dpn-pink hover:bg-dpn-raspberry text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Plus className="h-4 w-4" />
              <span>Create Event</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Events List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Events ({normalizedEvents.length})</h3>
        </div>
        <div className="p-6">
          {eventsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dpn-pink mx-auto mb-4"></div>
                             <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.LOADING.EVENTS}</p>
            </div>
          ) : normalizedEvents.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                             <h3 className="text-lg font-medium text-gray-900 mb-2">{DASHBOARD_CONFIG.MESSAGES.NO_DATA.EVENTS}</h3>
               <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.NO_DATA.TRY_ADJUSTING_FILTERS}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {normalizedEvents.map((event: any) => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    onViewDetails={handleViewEventDetails}
                    onEdit={handleEditEvent}
                    canEdit={true}
                    user={user}
                  />
                ))}
              </div>
              
              {/* Pagination */}
              {pagination && (
                <Pagination 
                  currentPage={pagination.currentPage || currentPage}
                  totalPages={pagination.totalPages || 1}
                  onPageChange={handlePageChange}
                  className="mt-6"
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  const renderCommunityUsersTab = () => (
    <div className="bg-white rounded-lg shadow">
      <CommunityUserManagement />
    </div>
  );

  return (
          <div className="min-h-screen bg-gray-50">
      {/* Event Details Modal */}
       <EventDetailModal
         event={selectedEvent}
         isOpen={showEventModal}
         onClose={() => setShowEventModal(false)}
         onEdit={handleEditEvent}
         canEdit={true}
       />
       
       {/* Bulk Upload Modal */}
       {showBulkUploadModal && (
         <BulkEventUpload 
           onClose={() => setShowBulkUploadModal(false)}
           onSuccess={handleBulkUploadSuccess}
         />
       )}
       
       {/* Create Event Modal */}
       {showCreateEventModal && (
         <EventFormWithMap
           event={null}
           onClose={() => setShowCreateEventModal(false)}
           onSubmit={handleCreateEvent}
           loading={eventFormLoading}
           title="Create New Event"
         />
       )}

       {/* Edit Event Modal */}
       {showEditModal && selectedEvent && (
         <EventFormWithMap
           event={selectedEvent}
           onClose={() => {
             setShowEditModal(false);
             setSelectedEvent(null);
           }}
           onSubmit={handleUpdateEvent}
           loading={eventFormLoading}
           title="Edit Event"
         />
       )}
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Shield className="h-8 w-8 text-dpn-pink" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Committee Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Welcome back, {user.role}</p>
              </div>
            </div>
            {/* <div className="flex items-center space-x-4">
              <button
                onClick={() => handleNavigation('/community')}
                className="flex items-center space-x-2 text-dpn-olive hover:text-dpn-pink transition-colors duration-200"
              >
                <Home className="h-5 w-5" />
                <span>Home</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-dpn-olive hover:text-dpn-pink transition-colors duration-200"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                activeTab === 'overview'
                  ? 'text-dpn-pink border-b-2 border-dpn-pink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                activeTab === 'events'
                  ? 'text-dpn-pink border-b-2 border-dpn-pink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Events
            </button>
            <button
              onClick={() => setActiveTab('community-users')}
              className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                activeTab === 'community-users'
                  ? 'text-dpn-pink border-b-2 border-dpn-pink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Committee Users
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'events' && renderEventsTab()}
        {activeTab === 'community-users' && renderCommunityUsersTab()}
      </div>
    </div>
  );
};

export default CommunityAdminDashboard;
